import { IonIcon } from "@ionic/react";
import {
  alertCircleOutline,
  bulbOutline,
  checkmarkCircleOutline,
  informationCircleOutline,
} from "ionicons/icons";
import { ReactElement } from "react";
import styled from "styled-components";
import InlineHtml from "../InlineHtml";

type CalloutTone = "info" | "tip" | "success" | "warning";

interface CalloutData {
  text?: string;
  tone?: CalloutTone;
}

/**
 * Tinted attention box. 'info' rides the theme accent (stays on-brand per study);
 * tip/success/warning use conventional semantic hues — deliberately theme-independent
 * so a warning reads as a warning in every study.
 */
const TONES: Record<CalloutTone, { icon: string; ink: string; bg: string }> = {
  info: {
    icon: informationCircleOutline,
    ink: "var(--app-color-accent, #2db6b4)",
    bg: "rgba(var(--app-color-accent-rgb, 45, 182, 180), 0.1)",
  },
  tip: {
    icon: bulbOutline,
    ink: "#7c5db3",
    bg: "rgba(124, 93, 179, 0.1)",
  },
  success: {
    icon: checkmarkCircleOutline,
    ink: "#2c7a57",
    bg: "rgba(44, 122, 87, 0.1)",
  },
  warning: {
    icon: alertCircleOutline,
    ink: "#b45309",
    bg: "rgba(180, 83, 9, 0.1)",
  },
};

const Box = styled.div<{ $bg: string; $ink: string }>`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${({ $bg }) => $bg};
  border-left: 3px solid ${({ $ink }) => $ink};

  ion-icon {
    flex: none;
    font-size: 22px;
    margin-top: 1px;
    color: ${({ $ink }) => $ink};
  }
`;

const Text = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.55;
  white-space: pre-line;
  color: var(--app-color-text, #041723);

  a {
    color: var(--app-color-accent, #2db6b4);
    text-decoration: underline;
  }
`;

const CalloutBlock = ({ data }: { data: CalloutData }): ReactElement | null => {
  if (!data.text) return null;
  const tone = TONES[data.tone ?? "info"] ?? TONES.info;
  return (
    <Box $bg={tone.bg} $ink={tone.ink}>
      <IonIcon icon={tone.icon} aria-hidden="true" />
      <Text>
        <InlineHtml html={data.text} />
      </Text>
    </Box>
  );
};

export default CalloutBlock;
